import { useTranslation } from 'react-i18next';
import { CircleCheckBig, Clock, Package, Truck } from 'lucide-react';
import { formatDate, formatOrderStatus } from '~/utils';
import { useSelector } from 'react-redux';
import type { RootState } from '~/redux/store.ts';
import { type OrderStatus } from '~/types/orders.ts';
import { OrderStatusCard } from '~/components/cards/OrderStatusCard';
import { ShippedCard } from '~/components/cards/ShippedCard';
import { OrderStatusProgress } from '~/pages/order-view/sections/OrderStatusProgress';

type OrderProgressTrackerOrder = {
  status: string;
  shippedDate?: string | null;
  deliveryStartDate?: string | null;
  deliveryEndDate?: string | null;
};

export function OrderProgressTrackerCardv2({
  order,
}: {
  order: OrderProgressTrackerOrder;
}) {
  const { t } = useTranslation('order');
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const shippedDate = order.shippedDate;
  const deliveryStartDate = order.deliveryStartDate;
  const deliveryEndDate = order.deliveryEndDate;

  const trackingMessage =
    role === 'BUYER'
      ? t('order.pending_seller_confirmation_message')
      : t('order.pendingSellerConfirmationDescription');

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1 flex items-center">
          <div className="flex flex-col items-center flex-1">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2 bg-prim text-white">
              <Package size={20} />
            </div>
            <p className="text-xs font-medium text-center text-gray-500">
              {t('order.placed')}
            </p>
          </div>
          <div className="flex-1 h-0.5 mx-2 bg-gray-200"></div>
        </div>
        <div className="flex-1 flex items-center">
          <div className="flex flex-col items-center flex-1">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2 bg-gray-100 text-gray-400">
              <CircleCheckBig size={20} />
            </div>
            <p className="text-xs font-medium text-center text-gray-500">
              {t('order.confirmed')}
            </p>
          </div>
          <div className="flex-1 h-0.5 mx-2 bg-gray-200"></div>
        </div>
        <div className="flex-1 flex items-center">
          <div className="flex flex-col items-center flex-1">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2 bg-gray-100 text-gray-400">
              <Truck size={20} />
            </div>
            <p className="text-xs font-medium text-center text-gray-500">
              {t('order.shipped')}
            </p>
          </div>
          <div className="flex-1 h-0.5 mx-2 bg-gray-200"></div>
        </div>
        <div className="flex-1 flex items-center">
          <div className="flex flex-col items-center flex-1">
            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-2 bg-gray-100 text-gray-400">
              <CircleCheckBig size={20} />
            </div>
            <p className="text-xs font-medium text-center text-gray-500">
              {t('order.delivered')}
            </p>
          </div>
        </div>
      </div>
      <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
        <div className="flex items-start gap-2">
          <Clock size={18} />
          <div>
            <p className="text-sm text-orange-900 font-medium mb-1">
              {t('order.status')}: {formatOrderStatus(order.status, role)}
            </p>
            <p className="text-xs text-orange-700">{trackingMessage}</p>
          </div>
        </div>
      </div>
      {order.status == 'SHIPPED' && (
        <div className="mt-4 space-y-4">
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-start gap-2 mb-2">
              <Truck size="18" className="text-purple-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-purple-900 mb-1">
                  {t('order.order_shipped_status')}
                </p>
                <div className="space-y-1">
                  <p className="text-xs text-purple-700">
                    <span className="font-medium">{t('order.shipped_on')}</span>{' '}
                    {shippedDate && <span>{formatDate(shippedDate)}</span>}
                  </p>
                  <p className="text-xs text-purple-700">
                    <span className="font-medium">
                      {t('order.estimated_delivery_date')}
                    </span>{' '}
                    {deliveryStartDate && (
                      <span>{formatDate(deliveryStartDate)}</span>
                    )}
                    {deliveryEndDate && (
                      <span>- {formatDate(deliveryEndDate)}</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function OrderProgressTrackerCard({
  order,
}: {
  order: {
    status: string;
    shippedDate?: string | null;
    deliveryStartDate?: string | null;
    deliveryEndDate?: string | null;
    receivedDate?: string | null;
  };
}) {
  const orderStatus = order.status as OrderStatus;

  return (
    <div>
      <OrderStatusProgress status={orderStatus} />
      <OrderStatusCard
        orderStatus={orderStatus}
        deliveredDate={order.receivedDate}
      />

      {orderStatus == 'SHIPPED' && (
        <div className="mt-4 space-y-4">
          <ShippedCard
            shippedDate={order.shippedDate}
            deliveryStartDate={order.deliveryStartDate}
            deliveryEndDate={order.deliveryEndDate}
          />
        </div>
      )}
    </div>
  );
}
